/* ============================================================
   WANCASA — Apple Direction v1
   Shared behaviour for wancasa-* sections.
   ============================================================ */
(function () {
  'use strict';

  /* ----------------------------------------------------------
     Scroll story — "One shape. Four pours."
     Pins the stage and translates the panel track horizontally
     as the user scrolls. The motion is damped (lerp towards the
     scroll target) for an inertial, Apple-like feel. All slide
     images are force-preloaded on connect so nothing pops in
     mid-swipe. Falls back to a swipeable rail on reduced motion.
     ---------------------------------------------------------- */
  if (!customElements.get('wancasa-scroll-story')) {
    customElements.define(
      'wancasa-scroll-story',
      class WancasaScrollStory extends HTMLElement {
        connectedCallback() {
          this.spacer = this.querySelector('.wc-story__spacer');
          this.stage = this.querySelector('.wc-story__stage');
          this.track = this.querySelector('.wc-story__track');
          this.barFill = this.querySelector('[data-story-bar]');
          this.mugs = Array.from(this.querySelectorAll('.wc-story__mug'));
          this.labels = Array.from(this.querySelectorAll('.wc-story__label'));
          this.wipeA = Array.from(this.querySelectorAll('[data-wipe="a"]'));
          this.wipeB = Array.from(this.querySelectorAll('[data-wipe="b"]'));
          if (!this.spacer || !this.stage || !this.track || this.mugs.length === 0) return;

          this.preloadImages();

          /* Per-slide stage darkness (0 = light, 1 = dark). Rendered as a
             continuous curve so the stage crossfades between neighbours
             instead of snapping at slide boundaries. */
          this.bgSeq = (this.dataset.bgSeq || '')
            .split(',')
            .map(function (v) { return parseFloat(v) ? 1 : 0; });
          if (this.bgSeq.length !== this.mugs.length) {
            this.bgSeq = this.mugs.map(function () { return 0; });
          }
          this.bgVaries = this.bgSeq.some(function (v, i, arr) { return v !== arr[0]; });

          this.current = 0;   // damped progress actually rendered
          this.target = 0;    // raw progress derived from scroll
          this.rafId = null;
          this.running = false;
          this.M = null;

          this.reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
          this.onScroll = this.onScroll.bind(this);
          this.onResize = this.onResize.bind(this);
          this.tick = this.tick.bind(this);
          this.applyMode = this.applyMode.bind(this);

          this.reduced.addEventListener
            ? this.reduced.addEventListener('change', this.applyMode)
            : this.reduced.addListener(this.applyMode);
          this.applyMode();
        }

        disconnectedCallback() {
          this.teardown();
        }

        preloadImages() {
          this.querySelectorAll('img').forEach((img) => {
            img.loading = 'eager';
            img.decoding = 'async';
            const src = img.currentSrc || img.src;
            if (src) {
              const pre = new Image();
              if (img.srcset) pre.srcset = img.srcset;
              if (img.sizes) pre.sizes = img.sizes;
              pre.src = src;
            }
          });
        }

        applyMode() {
          if (this.reduced.matches) {
            this.teardown();
            this.classList.add('wc-story--rail');
            this.mugs.forEach((m) => { m.style.transform = ''; });
            this.labels.forEach((l) => { l.style.opacity = ''; });
            this.wipeA.concat(this.wipeB).forEach((el) => {
              el.style.webkitMaskImage = '';
              el.style.maskImage = '';
            });
            ['--wc-story-bg', '--wc-story-ink', '--wc-story-muted', '--wc-story-rule', '--wc-story-shadow']
              .forEach((v) => this.style.removeProperty(v));
          } else {
            this.classList.remove('wc-story--rail');
            window.addEventListener('scroll', this.onScroll, { passive: true });
            window.addEventListener('resize', this.onResize, { passive: true });
            this.measure();
            this.onScroll();
          }
        }

        teardown() {
          window.removeEventListener('scroll', this.onScroll);
          window.removeEventListener('resize', this.onResize);
          if (this.rafId) window.cancelAnimationFrame(this.rafId);
          this.rafId = null;
          this.running = false;
        }

        /* Read the choreography constants once per layout change. They live
           in CSS custom properties so the mobile breakpoint only overrides
           numbers — never logic. */
        measure() {
          const cs = window.getComputedStyle(this);
          const num = (name, fallback) => {
            const v = parseFloat(cs.getPropertyValue(name));
            return isNaN(v) ? fallback : v;
          };
          const img = this.mugs[0].querySelector('img');
          const rect = img ? img.getBoundingClientRect() : { width: 0 };
          const mugW = rect.width || 1;

          this.M = {
            stageW: this.stage.clientWidth,
            mugW: mugW,
            slot: mugW * num('--wc-story-slot', 1.55),
            feather: num('--wc-story-feather', 60),
            show: num('--wc-story-show', 0.5),
            /* the bundled cut-outs carry ~4% transparent padding, so the
               glass edge sits inside the image box */
            inset: mugW * 0.04,
            clear: mugW * num('--wc-story-clear', 0.08)
          };
        }

        onResize() {
          this.measure();
          this.onScroll();
        }

        onScroll() {
          const rect = this.spacer.getBoundingClientRect();
          const viewportH = window.innerHeight;
          const total = rect.height - viewportH;
          if (total <= 0) return;
          this.target = Math.min(1, Math.max(0, -rect.top / total));
          if (!this.running) {
            this.running = true;
            this.rafId = window.requestAnimationFrame(this.tick);
          }
        }

        tick() {
          // damped follow: current eases towards target each frame
          const diff = this.target - this.current;
          this.current += diff * 0.085;
          if (Math.abs(diff) < 0.0004) this.current = this.target;

          this.render(this.current);

          if (this.current !== this.target) {
            this.rafId = window.requestAnimationFrame(this.tick);
          } else {
            this.running = false;
            this.rafId = null;
          }
        }

        /* Mask one group of type against a vertical edge given in viewport px.
           'erase' keeps what is left of the edge, 'reveal' keeps what is right
           of it — which is exactly what a leading and a trailing edge do as the
           mugs sweep past. */
        wipe(els, mode, edge) {
          const f = this.M.feather;
          els.forEach((el) => {
            const r = el.getBoundingClientRect();
            if (!r.width) return;
            const x = Math.max(-f * 2, Math.min(r.width + f * 2, edge - r.left));
            const g =
              mode === 'erase'
                ? 'linear-gradient(to right, #000 0px, #000 ' + (x - f).toFixed(1) +
                  'px, rgba(0,0,0,0) ' + x.toFixed(1) + 'px)'
                : 'linear-gradient(to right, rgba(0,0,0,0) 0px, rgba(0,0,0,0) ' + x.toFixed(1) +
                  'px, #000 ' + (x + f).toFixed(1) + 'px)';
            el.style.webkitMaskImage = g;
            el.style.maskImage = g;
          });
        }

        render(progress) {
          if (!this.M) this.measure();
          const M = this.M;
          const n = this.mugs.length;

          /* First mug starts with `show` of it inside the right edge; the last
             mug ends with the same amount inside the left edge. */
          const edge = M.mugW * (0.5 - M.show);
          const startC = M.stageW + edge;
          const endC = -(n - 1) * M.slot - edge;
          const c0 = startC + (endC - startC) * progress;

          for (let i = 0; i < n; i++) {
            const c = c0 + i * M.slot;
            this.mugs[i].style.transform =
              'translate3d(' + (c - M.mugW / 2).toFixed(2) + 'px, -50%, 0)';
            if (this.labels[i]) {
              const near = 1 - Math.abs(c - M.stageW / 2) / (M.slot * 0.5);
              this.labels[i].style.opacity = Math.max(0, Math.min(1, near)).toFixed(3);
            }
          }

          this.wipe(this.wipeA, 'erase', c0 - M.mugW / 2 + M.inset - M.clear);
          this.wipe(this.wipeB, 'reveal', c0 + (n - 1) * M.slot + M.mugW / 2 - M.inset + M.clear);

          if (this.barFill) {
            this.barFill.style.width = (progress * 100).toFixed(2) + '%';
          }

          this.renderStage(progress);
        }

        /* ---- stage palette -------------------------------------- */

        stageDarkness(progress) {
          const seq = this.bgSeq;
          if (seq.length < 2) return seq[0] || 0;
          const p = progress * (seq.length - 1);
          const i = Math.min(seq.length - 2, Math.max(0, Math.floor(p)));
          const f = Math.min(1, Math.max(0, p - i));
          const eased = f * f * (3 - 2 * f);          // smoothstep
          return seq[i] + (seq[i + 1] - seq[i]) * eased;
        }

        renderStage(progress) {
          if (!this.bgVaries) return;
          const k = this.stageDarkness(progress);
          const mix = function (a, b) { return Math.round(a + (b - a) * k); };
          const bg = [mix(245, 5), mix(245, 5), mix(247, 5)];
          const ink = [mix(29, 255), mix(29, 255), mix(31, 255)];
          const mut = [mix(110, 161), mix(110, 161), mix(115, 166)];
          const rule = [mix(0, 255), mix(0, 255), mix(0, 255)];
          const st = this.style;
          st.setProperty('--wc-story-bg', 'rgb(' + bg.join(',') + ')');
          st.setProperty('--wc-story-ink', 'rgb(' + ink.join(',') + ')');
          st.setProperty('--wc-story-muted', 'rgb(' + mut.join(',') + ')');
          st.setProperty('--wc-story-rule', rule.join(','));
          st.setProperty('--wc-story-shadow', (0.10 + 0.45 * k).toFixed(3));
        }
      }
    );
  }

  /* ----------------------------------------------------------
     Product gallery — mobile carousel

     Desktop keeps the stacked grid (controls are display:none).
     On mobile the same track becomes a scroll-snap carousel; this
     element wires up prev/next buttons and the thumbnail row, and
     keeps them in sync with native swipe scrolling.
     ---------------------------------------------------------- */
  if (!customElements.get('wancasa-gallery')) {
    customElements.define(
      'wancasa-gallery',
      class WancasaGallery extends HTMLElement {
        connectedCallback() {
          this.track = this.querySelector('[data-wc-gal-track]');
          this.slides = Array.from(this.querySelectorAll('.wc-product__photo'));
          this.thumbs = Array.from(this.querySelectorAll('[data-wc-gal-thumb]'));
          if (!this.track || this.slides.length < 2) return;

          this.index = 0;
          this.pending = null;

          this.track.addEventListener('scroll', () => {
            if (this.pending) return;
            this.pending = window.requestAnimationFrame(() => {
              this.pending = null;
              this.sync();
            });
          }, { passive: true });

          this.thumbs.forEach((t, i) => t.addEventListener('click', () => this.go(i)));

          window.addEventListener('resize', () => this.go(this.index, false), { passive: true });
          this.sync();
        }

        go(i, smooth) {
          const n = this.slides.length;
          i = Math.max(0, Math.min(n - 1, i));
          const w = this.track.clientWidth;
          this.track.scrollTo({ left: i * w, behavior: smooth === false ? 'auto' : 'smooth' });
        }

        sync() {
          const w = this.track.clientWidth;
          if (!w) return;
          const i = Math.max(0, Math.min(this.slides.length - 1, Math.round(this.track.scrollLeft / w)));
          if (i === this.index && this.synced) return;
          this.index = i;
          this.synced = true;
          this.thumbs.forEach((t, k) => {
            if (k === i) t.setAttribute('aria-current', 'true');
            else t.removeAttribute('aria-current');
          });
          const active = this.thumbs[i];
          if (active && active.scrollIntoView) {
            active.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'smooth' });
          }
        }
      }
    );
  }

  /* ----------------------------------------------------------
     Accordion — animated <details> open/close

     Uses the Web Animations API rather than CSS transitions:
     a transition on an element whose start value is committed in
     the same task gets retargeted mid-flight, which is what made
     the panel hitch twice per toggle. WAAPI lets us state the
     exact from/to pair once, and reverse cleanly on rapid clicks.

     Height is animated on an outer wrapper while the padding
     lives on an inner one, so the text is never squeezed and we
     never have to measure a box that is itself being animated.
     ---------------------------------------------------------- */
  function initAccordions(root) {
    var items = (root || document).querySelectorAll('[data-wc-accordion]');
    Array.prototype.forEach.call(items, function (details) {
      if (details.dataset.wcAccordionReady) return;
      details.dataset.wcAccordionReady = '1';

      var summary = details.querySelector('summary');
      var body = details.querySelector('[data-wc-acc-body]');
      var inner = details.querySelector('[data-wc-acc-inner]');
      if (!summary || !body || !inner) return;

      if (details.open) details.setAttribute('data-wc-open', '');

      // No WAAPI (or reduced motion): keep the plain native behaviour.
      if (typeof body.animate !== 'function') return;

      var anim = null;

      function settle(opening) {
        anim = null;
        body.style.height = '';
        if (!opening) details.open = false;
      }

      function play(opening) {
        // Read the live height before cancelling, so a mid-flight
        // reversal starts exactly where the panel currently is.
        var from = 0;
        if (anim) {
          from = body.getBoundingClientRect().height;
          anim.cancel();
          anim = null;
        } else if (!opening) {
          from = body.getBoundingClientRect().height;
        }

        if (opening && !details.open) details.open = true;

        // inner is never height-constrained, so this is always the
        // true content height — one layout read per toggle.
        var to = opening ? inner.getBoundingClientRect().height : 0;

        anim = body.animate(
          [{ height: from + 'px' }, { height: to + 'px' }],
          { duration: 260, easing: 'cubic-bezier(.4, 0, .2, 1)', fill: 'both' }
        );

        anim.onfinish = function () {
          if (anim) anim.cancel();
          settle(opening);
        };

      }

      summary.addEventListener('click', function (event) {
        event.preventDefault();

        var opening = !details.hasAttribute('data-wc-open');
        // flip the +/x marker immediately, independent of the animation
        if (opening) {
          details.setAttribute('data-wc-open', '');
        } else {
          details.removeAttribute('data-wc-open');
        }

        if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
          if (anim) { anim.cancel(); anim = null; }
          body.style.height = '';
          details.open = opening;
          return;
        }

        play(opening);
      });
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initAccordions(); });
  } else {
    initAccordions();
  }
  // Shopify theme editor re-renders sections without a page reload
  document.addEventListener('shopify:section:load', function (event) {
    initAccordions(event.target);
  });


  /* ----------------------------------------------------------
     FAQ page — category pills filter the accordion list.
     Progressive enhancement: with JS off every question shows.
     ---------------------------------------------------------- */
  function initFaqFilter(root) {
    var groups = (root || document).querySelectorAll('[data-wc-faq]');
    Array.prototype.forEach.call(groups, function (group) {
      if (group.dataset.wcFaqReady) return;
      group.dataset.wcFaqReady = '1';

      var pills = group.querySelectorAll('[data-wc-faq-pill]');
      var items = group.querySelectorAll('[data-wc-faq-item]');
      if (!pills.length) return;

      Array.prototype.forEach.call(pills, function (pill) {
        pill.addEventListener('click', function (event) {
          event.preventDefault();
          var want = pill.getAttribute('data-wc-faq-pill');

          Array.prototype.forEach.call(pills, function (p) {
            p.setAttribute('aria-current', p === pill ? 'true' : 'false');
          });
          Array.prototype.forEach.call(items, function (item) {
            var cat = item.getAttribute('data-wc-faq-item');
            item.hidden = !(want === '*' || cat === want);
          });
        });
      });
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initFaqFilter(); });
  } else {
    initFaqFilter();
  }
  document.addEventListener('shopify:section:load', function (event) {
    initFaqFilter(event.target);
  });

  /* ----------------------------------------------------------
     Cart page — quantity steppers submit the native cart form,
     so the page works identically with JS disabled.
     ---------------------------------------------------------- */
  function initCartSteppers(root) {
    var steppers = (root || document).querySelectorAll('[data-wc-qty]');
    Array.prototype.forEach.call(steppers, function (wrap) {
      if (wrap.dataset.wcQtyReady) return;
      wrap.dataset.wcQtyReady = '1';

      var input = wrap.querySelector('input');
      var form = wrap.closest('form');
      if (!input) return;

      Array.prototype.forEach.call(wrap.querySelectorAll('button[data-wc-step]'), function (btn) {
        btn.addEventListener('click', function (event) {
          event.preventDefault();
          var step = parseInt(btn.getAttribute('data-wc-step'), 10) || 0;
          var next = (parseInt(input.value, 10) || 0) + step;
          if (next < 0) next = 0;
          input.value = next;
          if (form) form.submit();
        });
      });
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initCartSteppers(); });
  } else {
    initCartSteppers();
  }
  document.addEventListener('shopify:section:load', function (event) {
    initCartSteppers(event.target);
  });

  /* ----------------------------------------------------------
     Product package picker
     Radio cards switch the selected variant: update the hidden
     [name=id], the displayed price and the ATC button state.
     Works inside the theme's <product-form> AJAX submit.
     ---------------------------------------------------------- */
  if (!customElements.get('wancasa-package-picker')) {
    customElements.define(
      'wancasa-package-picker',
      class WancasaPackagePicker extends HTMLElement {
        connectedCallback() {
          this.section = this.closest('.wc-product');
          if (!this.section) return;
          this.radios = Array.from(this.querySelectorAll('input[type="radio"][data-variant-id]'));
          this.priceEl = this.section.querySelector('[data-wc-price]');
          this.compareEl = this.section.querySelector('[data-wc-compare-price]');
          this.selectedLabel = this.section.querySelector('[data-wc-selected-label]');
          this.atcButton = this.section.querySelector('.wc-product__atc');
          this.idInput = this.section.querySelector('form [name="id"]');
          this.radios.forEach((radio) => {
            radio.addEventListener('change', () => this.onChange(radio));
          });
        }

        onChange(radio) {
          if (this.idInput) this.idInput.value = radio.dataset.variantId;
          if (this.priceEl && radio.dataset.price) this.priceEl.textContent = radio.dataset.price;
          if (this.compareEl) {
            if (radio.dataset.comparePrice) {
              this.compareEl.textContent = radio.dataset.comparePrice;
              this.compareEl.hidden = false;
            } else {
              this.compareEl.hidden = true;
            }
          }
          if (this.selectedLabel) this.selectedLabel.textContent = radio.dataset.selectedText || '';
          if (this.atcButton) {
            const available = radio.dataset.available === 'true';
            this.atcButton.disabled = !available;
            const label = this.atcButton.querySelector('[data-wc-atc-text]');
            if (label) {
              label.textContent = available
                ? this.atcButton.dataset.labelAdd
                : this.atcButton.dataset.labelSoldout;
            }
          }
          const url = new URL(window.location.href);
          url.searchParams.set('variant', radio.dataset.variantId);
          window.history.replaceState({}, '', url.toString());
        }
      }
    );
  }
})();

/* ============================================================
   FOOTER LOCALIZATION — submit the Shopify localization form as
   soon as a country is picked, so the currency actually switches.
   ============================================================ */
(function () {
  function bind(select) {
    if (select.dataset.wcLocaleBound) return;
    select.dataset.wcLocaleBound = '1';
    select.addEventListener('change', function () {
      var form = select.closest('form');
      if (form) form.submit();
    });
  }

  function init(root) {
    (root || document).querySelectorAll('[data-wc-locale]').forEach(bind);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(); });
  } else {
    init();
  }

  /* the theme editor re-renders sections without a page load */
  document.addEventListener('shopify:section:load', function (e) { init(e.target); });
})();
